// Booking page config — the branding + questions + personal share link block.
// Fully controlled: parent owns `form` state and `set` mutator.
import { toast } from "sonner";
import {
  Globe, Copy, Link2, Calendar as CalendarIcon,
  Plus, Trash2, Loader2, Save,
} from "lucide-react";
import { Section, Field } from "./common";

export default function BookingSection({ form, set, save, saving, settings }) {
  const questions = form.booking_questions || [];

  const patchQuestion = (idx, patch) => {
    const next = [...questions];
    next[idx] = { ...next[idx], ...patch };
    set("booking_questions", next);
  };

  const removeQuestion = (idx) => {
    const next = [...questions];
    next.splice(idx, 1);
    set("booking_questions", next);
  };

  return (
    <Section
      title="Booking Page"
      subtitle="Customize the page prospects see when they click your booking link. Add a logo, intro, pre-call questions — and share your personal link directly with warm prospects."
      icon={<Globe className="w-4 h-4" />}
    >
      {/* Personal share link */}
      <div className="border border-zinc-200 bg-zinc-50 p-4 mb-6" data-testid="personal-link-block">
        <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Your personal booking link</div>
        <div className="flex items-center gap-2 mb-2">
          <code className="flex-1 px-3 py-2 bg-white border border-zinc-200 text-sm text-zinc-800 break-all font-mono" data-testid="personal-booking-url">
            {settings?.personal_booking_url || "—"}
          </code>
          <button
            type="button"
            onClick={() => { navigator.clipboard.writeText(settings?.personal_booking_url || ""); toast.success("Copied"); }}
            data-testid="btn-copy-personal-url"
            className="px-3 py-2 border border-zinc-300 text-[10px] font-bold uppercase tracking-wider hover:bg-zinc-100 flex items-center gap-1"
          >
            <Copy className="w-3 h-3" /> Copy
          </button>
          <a
            href={settings?.personal_booking_url || "#"}
            target="_blank"
            rel="noreferrer"
            data-testid="btn-open-personal-url"
            className="px-3 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-blue-50 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"
          >
            <Link2 className="w-3 h-3" /> Open
          </a>
        </div>
        <Field label="Slug">
          <input
            type="text"
            value={form.public_booking_slug || ""}
            onChange={(e) => set("public_booking_slug", e.target.value)}
            data-testid="input-booking-slug"
            placeholder="guillermo"
            className="w-full max-w-xs px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
          />
        </Field>
        <div className="text-[10px] text-zinc-500 mt-1.5">
          Letters, numbers and dashes only. This is a <strong>multi-use</strong> link — share it with anyone, anytime.
        </div>
      </div>

      {/* Branding */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Field label="Logo URL">
          <input
            type="url"
            value={form.booking_logo_url || ""}
            onChange={(e) => set("booking_logo_url", e.target.value)}
            placeholder="https://yourdomain.com/logo.png"
            data-testid="input-booking-logo-url"
            className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]"
          />
        </Field>
        <Field label="Brand color (hex)">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.booking_brand_color || "#002FA7"}
              onChange={(e) => set("booking_brand_color", e.target.value)}
              data-testid="input-booking-brand-color"
              className="w-12 h-10 border border-zinc-300 cursor-pointer"
            />
            <input
              type="text"
              value={form.booking_brand_color || ""}
              onChange={(e) => set("booking_brand_color", e.target.value)}
              placeholder="#002FA7"
              className="flex-1 px-3 py-2.5 border border-zinc-300 text-sm font-mono focus:outline-none focus:border-[#002FA7]"
            />
          </div>
        </Field>
        <Field label="Headline">
          <input
            type="text"
            value={form.booking_headline || ""}
            onChange={(e) => set("booking_headline", e.target.value)}
            placeholder="30 Minute Meeting"
            data-testid="input-booking-headline"
            className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]"
          />
        </Field>
        <Field label="Subheadline">
          <input
            type="text"
            value={form.booking_subheadline || ""}
            onChange={(e) => set("booking_subheadline", e.target.value)}
            placeholder="Scalater: Your Strategic Shopify Partner"
            data-testid="input-booking-subheadline"
            className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]"
          />
        </Field>
        <div className="md:col-span-2">
          <Field label="Description / intro paragraph">
            <textarea
              rows={4}
              value={form.booking_description || ""}
              onChange={(e) => set("booking_description", e.target.value)}
              placeholder="Whether you need to elevate your established Shopify brand, build a sustainable strategy for long-term success, or unlock new revenue streams, our team helps you scale with confidence."
              data-testid="input-booking-description"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] resize-y"
            />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Web conferencing note">
            <input
              type="text"
              value={form.booking_web_conf_note || ""}
              onChange={(e) => set("booking_web_conf_note", e.target.value)}
              placeholder="Web conferencing details provided upon confirmation."
              data-testid="input-booking-web-conf-note"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]"
            />
          </Field>
        </div>
      </div>

      {/* Questions */}
      <div className="border-t border-zinc-100 pt-5 mb-5" data-testid="booking-questions-block">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-sm uppercase tracking-wider text-zinc-800">Pre-call questions</h3>
            <div className="text-xs text-zinc-500 mt-0.5">
              Prospects must answer these before the slot is confirmed. Use sparingly — long forms hurt conversion.
            </div>
          </div>
          <button
            type="button"
            onClick={() => set("booking_questions", [
              ...questions,
              { id: `q_${Math.random().toString(36).slice(2, 10)}`, label: "", kind: "text", required: true, options: [], placeholder: "" },
            ])}
            data-testid="btn-add-booking-question"
            className="border border-zinc-300 hover:border-[#002FA7] text-zinc-800 hover:text-[#002FA7] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Add question
          </button>
        </div>

        <div className="space-y-3">
          {questions.map((q, idx) => (
            <div key={q.id || idx} className="border border-zinc-200 p-3 bg-zinc-50/50" data-testid={`booking-question-${idx}`}>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto] gap-2 mb-2">
                <input
                  type="text"
                  value={q.label || ""}
                  onChange={(e) => patchQuestion(idx, { label: e.target.value })}
                  placeholder="e.g. What's your monthly revenue?"
                  data-testid={`input-question-label-${idx}`}
                  className="px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]"
                />
                <select
                  value={q.kind || "text"}
                  onChange={(e) => patchQuestion(idx, { kind: e.target.value })}
                  data-testid={`input-question-kind-${idx}`}
                  className="px-3 py-2 border border-zinc-300 text-sm bg-white"
                >
                  <option value="text">Short text</option>
                  <option value="textarea">Long text</option>
                  <option value="select">Dropdown</option>
                  <option value="yes_no">Yes / No</option>
                </select>
                <button
                  type="button"
                  onClick={() => removeQuestion(idx)}
                  data-testid={`btn-remove-question-${idx}`}
                  className="px-3 py-2 border border-zinc-300 hover:border-red-400 hover:text-red-600 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              {q.kind === "select" && (
                <input
                  type="text"
                  value={(q.options || []).join(", ")}
                  onChange={(e) => patchQuestion(idx, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  placeholder="Comma-separated options: <$5k MRR, $5-25k MRR, $25k+ MRR"
                  data-testid={`input-question-options-${idx}`}
                  className="w-full px-3 py-2 border border-zinc-300 text-xs font-mono focus:outline-none focus:border-[#002FA7]"
                />
              )}
              <label className="flex items-center gap-2 text-xs text-zinc-700 mt-2">
                <input
                  type="checkbox"
                  checked={!!q.required}
                  onChange={(e) => patchQuestion(idx, { required: e.target.checked })}
                  data-testid={`input-question-required-${idx}`}
                />
                Required to confirm booking
              </label>
            </div>
          ))}
          {questions.length === 0 && (
            <div className="text-xs text-zinc-400 italic text-center py-6 border border-dashed border-zinc-200">
              No questions yet. Click "Add question" to ask prospects something before they confirm a slot.
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-2">
        <a
          href={settings?.personal_booking_url || "/book/preview"}
          target="_blank"
          rel="noreferrer"
          data-testid="btn-preview-booking-page-bottom"
          className="border border-zinc-300 hover:border-[#002FA7] hover:text-[#002FA7] text-zinc-800 px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2"
        >
          <CalendarIcon className="w-3.5 h-3.5" />
          Preview live page
        </a>
        <button
          type="button" onClick={save} disabled={saving}
          data-testid="btn-save-booking-page"
          className="ml-auto bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save booking page
        </button>
      </div>
    </Section>
  );
}
